import Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { useEffect, useMemo, useRef, useState } from "react";
import { Layer, Stage, Transformer } from "react-konva";
import type {
  BoardScene,
  BoardShape,
  BoardToken,
} from "@/lib/canvas/board-schema";
import { clampShape, clampToken, emptyScene } from "@/lib/canvas/board-schema";
import { PITCH_H, PITCH_W, type Dims } from "@/lib/canvas/coords";
import {
  formationsForGameFormat,
  GAME_FORMATS,
  playerTokensFromFormation,
  type GameFormat,
} from "@/lib/canvas/formations";
import type { SharedShape } from "@/lib/canvas/shared-shape";
import {
  isToolAllowed,
  useReviewEditorStore,
  type EditorToolKind,
} from "@/stores/review-editor";
import { CanvasSelect } from "./components/CanvasSelect";
import { ColorPalette } from "./components/ColorPalette";
import { PitchBackground } from "./components/PitchBackground";
import { ShapeRenderer } from "./components/ShapeRenderer";
import { StrokeWidthSlider } from "./components/StrokeWidthSlider";
import { ToolGrid } from "./components/ToolGrid";
import { TokenRenderer } from "./components/TokenRenderer";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CanvasRailControls } from "./components/CanvasRailControls";
import { useUndoStack } from "@/lib/canvas/useUndoStack";
import { RosterPickerModal } from "@/features/coach-review/components/RosterPickerModal";
import type { Player } from "@/lib/types";

/**
 * Full tactical-board editor.
 *
 * Drag-state machine, driven by Konva stage events and persisted in
 * the `review-editor` store's `tactical` surface:
 *
 *   1. mousedown on empty stage with a shape tool armed:
 *      - arrow / line: pending shape with x1=x2 at the pointer
 *      - zone: pending zone with w=h=tiny near the pointer
 *      - freehand: pending shape with `points: [click]`
 *      - label: read text from the inline toolbar input (M7.5),
 *        place at pointer, disarm
 *      - player / ball: place a single token at pointer, disarm
 *      - select: clear selection (the void was clicked)
 *   2. mousemove during drag → update pending via the store
 *   3. mouseup → commit pending; reject degenerate shapes
 *
 * Selection: per-node onClick wires through `selectShape`. Zones get
 * a Konva.Transformer attached so resize-handles work for free
 * (replaces the legacy hand-rolled 8-handle code at tactical-
 * board.js:524-541). Other shapes are draggable but not resizable
 * through the Transformer (legacy behavior).
 *
 * Apply Formation: replaces existing player tokens after a confirm
 * dialog. Preserves the ball + every non-player shape. The "custom"
 * sentinel just tags the scene with formation metadata without
 * synthesizing positions.
 *
 * Keyboard shortcuts (legacy mapping): V/A/L/Z/T/F/P/B arm the
 * matching tool. Delete/Backspace removes the selected shape or
 * token. Esc disarms (back to select) and clears selection.
 */

const TACTICAL_TOOLS: readonly EditorToolKind[] = [
  "select",
  "arrow",
  "line",
  "zone",
  "label",
  "freehand",
  "player",
  "ball",
];

let _idCounter = 0;
function nextId(prefix: string): string {
  _idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idCounter}`;
}

export type TacticalBoardEditorProps = {
  scene: BoardScene;
  onSceneChange: (scene: BoardScene) => void;
  /** Stage width in pixels. Height derives from PITCH_H/PITCH_W. */
  width: number;
  /** When true, render only the pitch stage — no side toolbar.
   * Used by Coach Review focus mode, which renders an external
   * floating tool overlay that drives the same review-editor store.
   * The canvas's own side toolbar would conflict with the overlay. */
  hideToolbar?: boolean;
  /** Extra content appended to the right-rail toolbar, below the
   * built-in tools / color / stroke / formation / clear-board
   * controls. Coach Review uses this to stack the template
   * selector + composer in the same column as the tools so the
   * coach can type notes while watching the pitch. Ignored when
   * `hideToolbar` is true. */
  toolbarChildren?: React.ReactNode;
};

export function TacticalBoardEditor(props: TacticalBoardEditorProps) {
  const aspect = PITCH_H / PITCH_W;
  const width = Math.max(1, Math.round(props.width));
  const height = Math.max(1, Math.round(width * aspect));
  const dims: Dims = useMemo(() => ({ width, height }), [width, height]);

  const armedTool = useReviewEditorStore((s) => s.tactical.armedTool);
  const color = useReviewEditorStore((s) => s.tactical.color);
  const strokeWidth = useReviewEditorStore((s) => s.tactical.strokeWidth);
  const selectedId = useReviewEditorStore((s) => s.tactical.selectedShapeId);
  const pending = useReviewEditorStore((s) => s.tactical.pendingShape);
  const armTool = useReviewEditorStore((s) => s.armTool);
  const setColor = useReviewEditorStore((s) => s.setColor);
  const setStrokeWidth = useReviewEditorStore((s) => s.setStrokeWidth);
  const setSurface = useReviewEditorStore((s) => s.setSurface);
  const selectShape = useReviewEditorStore((s) => s.selectShape);
  const startPending = useReviewEditorStore((s) => s.startPending);
  const updatePending = useReviewEditorStore((s) => s.updatePending);
  const commitPending = useReviewEditorStore((s) => s.commitPending);
  const cancelPending = useReviewEditorStore((s) => s.cancelPending);
  const disarm = useReviewEditorStore((s) => s.disarm);

  // Make sure the store is pinned to the tactical surface whenever
  // this component renders (the demo route swaps surfaces).
  useEffect(() => {
    setSurface("tactical");
  }, [setSurface]);

  // ---- Local formation-picker state ----
  const [gameFormat, setGameFormat] = useState<GameFormat>(
    props.scene.game_format ?? "11v11",
  );
  const [formationId, setFormationId] = useState<string>(
    props.scene.formation ?? formationsForGameFormat(gameFormat)[0]?.id ?? "",
  );

  // M7.5 B3: inline label-text input — replaces the M4
  // `window.prompt` (which itself was a regression; legacy
  // explicitly rejected raw browser dialogs).
  const [labelText, setLabelText] = useState("");

  // M7.5 B4 / B6: styled confirms for clear-board + Apply
  // Formation, replacing `window.confirm`.
  const [confirm, setConfirm] = useState<
    | { kind: "clear-board" }
    | { kind: "apply-formation"; pendingFormationId: string }
    | null
  >(null);

  // M7.5 C1 / C2: Canvas-rail state — visibility toggle + the
  // multi-step undo stack (cap 10, clears on remount).
  const [canvasVisible, setCanvasVisible] = useState(true);
  const undoStack = useUndoStack<BoardScene>({ maxDepth: 10 });

  // M7.5 E1 / E3: token-binding state.
  //   - `bindingTokenId`: the player token whose `label` /
  //     `player_id` the coach is editing inline. Anchors the
  //     label input + opens the roster picker via "Pick player".
  //   - `formationLinkedMode`: when true, applying a formation
  //     pre-opens the picker per new anchor so the coach binds
  //     each in sequence. When false (Quick mode), tokens land
  //     anonymous and the coach can bind individuals later.
  //   - `linkingFormation`: the active per-anchor sequential
  //     apply state when Linked mode just placed N tokens.
  const [bindingTokenId, setBindingTokenId] = useState<string | null>(null);
  const [formationLinkedMode, setFormationLinkedMode] = useState(false);
  const [linkingFormation, setLinkingFormation] = useState<{
    tokenIds: string[];
    index: number;
  } | null>(null);

  /** Wrap every write to the scene so the previous snapshot is
   * pushed onto the undo stack first. Use this in place of
   * `props.onSceneChange` for any *user-initiated* write. The
   * Undo button itself uses `props.onSceneChange` directly so
   * undoing doesn't push the undone state back onto the stack. */
  const commitScene = (next: BoardScene) => {
    undoStack.push(props.scene);
    props.onSceneChange(next);
  };

  // ---- Stage + Transformer refs ----
  const stageRef = useRef<Konva.Stage | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);

  // Attach Transformer to the selected zone (only zones get resize
  // handles for now; other shapes are draggable but not resizable).
  useEffect(() => {
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    if (!stage || !transformer) return;
    if (!selectedId) {
      transformer.nodes([]);
      transformer.getLayer()?.batchDraw();
      return;
    }
    const shape = props.scene.shapes.find((s) => s.id === selectedId);
    if (shape?.kind === "zone") {
      // Konva nodes get a synthetic id from `name`/`id` props. We
      // attach via `findOne(`#${id}`)` — the ShapeRenderer doesn't
      // currently set an id on its inner node, so look up by name
      // (set below in the editor render path via the wrapping Group).
      const node = stage.findOne(`#${selectedId}`);
      if (node) {
        transformer.nodes([node]);
      } else {
        transformer.nodes([]);
      }
    } else {
      transformer.nodes([]);
    }
    transformer.getLayer()?.batchDraw();
  }, [selectedId, props.scene.shapes]);

  // ---- Keyboard shortcuts (gated on the data attribute below) ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      const key = e.key.toUpperCase();
      const map: Record<string, EditorToolKind> = {
        V: "select",
        A: "arrow",
        L: "line",
        Z: "zone",
        T: "label",
        F: "freehand",
        P: "player",
        B: "ball",
      };
      if (key in map) {
        e.preventDefault();
        armTool(map[key]!);
        return;
      }
      if (e.key === "Escape") {
        cancelPending();
        disarm();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        const filteredShapes = props.scene.shapes.filter((s) => s.id !== selectedId);
        const filteredTokens = props.scene.tokens.filter((t) => t.id !== selectedId);
        if (
          filteredShapes.length !== props.scene.shapes.length ||
          filteredTokens.length !== props.scene.tokens.length
        ) {
          commitScene({
            ...props.scene,
            shapes: filteredShapes,
            tokens: filteredTokens,
          });
          selectShape(null);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [armTool, cancelPending, disarm, selectedId, selectShape, props]);

  // ---- Stage event helpers ----
  function unitFromStageEvent(
    e: KonvaEventObject<MouseEvent | TouchEvent>,
  ): { x: number; y: number } | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    const p = stage.getPointerPosition();
    if (!p) return null;
    return { x: clamp01(p.x / dims.width), y: clamp01(p.y / dims.height) };
  }

  function handleMouseDown(
    e: KonvaEventObject<MouseEvent | TouchEvent>,
  ) {
    const onStageBackground = e.target === e.target.getStage();
    if (!isToolAllowed(armedTool, "tactical")) return;
    if (!onStageBackground) return; // per-shape onClick handles selection

    const pt = unitFromStageEvent(e);
    if (!pt) return;

    if (armedTool === "select") {
      selectShape(null);
      return;
    }
    if (armedTool === "arrow" || armedTool === "line") {
      const shape: SharedShape = {
        kind: armedTool,
        id: nextId(armedTool),
        x1: pt.x,
        y1: pt.y,
        x2: pt.x,
        y2: pt.y,
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "zone") {
      const shape: SharedShape = {
        kind: "zone",
        id: nextId("zone"),
        x: pt.x,
        y: pt.y,
        w: 0.001,
        h: 0.001,
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "freehand") {
      const shape: SharedShape = {
        kind: "freehand",
        id: nextId("freehand"),
        points: [pt],
        color,
        strokeWidth,
      };
      startPending(shape);
      return;
    }
    if (armedTool === "label") {
      // M7.5 B3: read from the inline `labelText` input instead
      // of `window.prompt`. Legacy parity
      // (`js/tactical-board.js:2229-2246` explicitly rejected
      // raw browser dialogs). Empty text ⇒ no-op + focus the
      // input via a side-channel? Keep it simple: just ignore
      // the click. The toolbar's input has its own placeholder.
      const trimmed = labelText.trim().slice(0, 80);
      if (!trimmed) return;
      const shape: SharedShape = {
        kind: "label",
        id: nextId("label"),
        x: pt.x,
        y: pt.y,
        text: trimmed,
        color,
        strokeWidth,
      };
      commitScene({
        ...props.scene,
        shapes: [...props.scene.shapes, shape],
      });
      // Drop the consumed text so the next click needs fresh
      // input (matches legacy single-use behaviour).
      setLabelText("");
      armTool("select");
      return;
    }
    if (armedTool === "player" || armedTool === "ball") {
      const token: BoardToken = {
        kind: armedTool,
        id: nextId(armedTool),
        x: pt.x,
        y: pt.y,
        label: armedTool === "player" ? "" : undefined,
        player_id: "",
      };
      commitScene({
        ...props.scene,
        tokens: [...props.scene.tokens, token],
      });
      armTool("select");
      return;
    }
  }

  function handleMouseMove(
    e: KonvaEventObject<MouseEvent | TouchEvent>,
  ) {
    if (!pending) return;
    const pt = unitFromStageEvent(e);
    if (!pt) return;
    if (pending.kind === "arrow" || pending.kind === "line") {
      updatePending({ ...pending, x2: pt.x, y2: pt.y });
      return;
    }
    if (pending.kind === "zone") {
      const x = Math.min(pending.x, pt.x);
      const y = Math.min(pending.y, pt.y);
      const w = Math.abs(pt.x - pending.x);
      const h = Math.abs(pt.y - pending.y);
      updatePending({ ...pending, x, y, w, h });
      return;
    }
    if (pending.kind === "freehand") {
      const lastPt = pending.points[pending.points.length - 1];
      if (
        lastPt &&
        Math.abs(lastPt.x - pt.x) + Math.abs(lastPt.y - pt.y) < 0.005
      ) {
        return; // skip near-duplicate
      }
      if (pending.points.length >= 200) {
        commitToScene();
        return;
      }
      updatePending({ ...pending, points: [...pending.points, pt] });
      return;
    }
  }

  function commitToScene() {
    const committed = commitPending();
    if (!committed) return;
    const finalShape = clampShape(committed) as BoardShape;
    // Reject degenerate shapes:
    if (
      finalShape.kind === "zone" &&
      (finalShape.w < 0.02 || finalShape.h < 0.02)
    ) {
      return;
    }
    if (finalShape.kind === "freehand" && finalShape.points.length < 2) {
      return;
    }
    if (
      (finalShape.kind === "arrow" || finalShape.kind === "line") &&
      Math.abs(finalShape.x1 - finalShape.x2) < 0.005 &&
      Math.abs(finalShape.y1 - finalShape.y2) < 0.005
    ) {
      return;
    }
    commitScene({
      ...props.scene,
      shapes: [...props.scene.shapes, finalShape],
    });
    selectShape(null);
  }

  function handleMouseUp() {
    if (!pending) return;
    commitToScene();
  }

  function replaceShape(shape: BoardShape) {
    const clamped = clampShape(shape);
    commitScene({
      ...props.scene,
      shapes: props.scene.shapes.map((s) =>
        s.id === clamped.id ? clamped : s,
      ),
    });
  }

  function replaceToken(token: BoardToken) {
    const clamped = clampToken(token);
    commitScene({
      ...props.scene,
      tokens: props.scene.tokens.map((t) =>
        t.id === clamped.id ? clamped : t,
      ),
    });
  }

  function handleTransformEnd(e: KonvaEventObject<Event>) {
    const node = e.target as Konva.Node;
    const id = node.id();
    const shape = props.scene.shapes.find((s) => s.id === id);
    if (!shape || shape.kind !== "zone") return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    const newWidth = node.width() * scaleX;
    const newHeight = node.height() * scaleY;
    const newX = node.x();
    const newY = node.y();
    const x = clamp01(newX / dims.width);
    const y = clamp01(newY / dims.height);
    const w = Math.max(0.02, Math.min(1 - x, newWidth / dims.width));
    const h = Math.max(0.02, Math.min(1 - y, newHeight / dims.height));
    replaceShape({ ...shape, x, y, w, h });
  }

  /** B6: Apply Formation now uses `<ConfirmDialog>` when player
   * tokens exist on the board (the destructive case). With no
   * existing players, apply directly — no confirm needed.
   * `<ConfirmDialog>` is in the JSX below; this just opens it. */
  function handleApplyFormation() {
    const hasPlayers = props.scene.tokens.some((t) => t.kind === "player");
    if (hasPlayers) {
      setConfirm({ kind: "apply-formation", pendingFormationId: formationId });
      return;
    }
    commitApplyFormation(formationId);
  }

  function commitApplyFormation(targetFormationId: string) {
    const newPlayers = playerTokensFromFormation(
      gameFormat,
      targetFormationId,
      () => nextId("player"),
    );
    if (newPlayers === null) {
      // Custom sentinel or unknown — record metadata, preserve tokens.
      commitScene({
        ...props.scene,
        game_format: gameFormat,
        formation: targetFormationId,
      });
      return;
    }
    const ballAndOthers: BoardToken[] = props.scene.tokens.filter(
      (t) => t.kind !== "player",
    );
    const safeTokens = [...ballAndOthers, ...newPlayers].slice(0, 40);
    commitScene({
      ...props.scene,
      game_format: gameFormat,
      formation: targetFormationId,
      tokens: safeTokens,
    });
    selectShape(null);

    // M7.5 E3: Linked mode follow-up — start a sequential
    // per-anchor picker over the new player-token ids.
    if (formationLinkedMode) {
      const newIds = newPlayers
        .map((t) => t.id)
        .filter((id): id is string => !!id);
      if (newIds.length > 0) {
        setLinkingFormation({ tokenIds: newIds, index: 0 });
      }
    }
  }

  /** Update a single token's `label` + `player_id` after the
   * coach picks from the roster — used by both E1 (single bind)
   * and E3 (Linked-mode sequential apply). */
  function bindPlayerToToken(tokenId: string, player: Player) {
    const labelFromPlayer = player.jersey_number
      ? `#${player.jersey_number} ${player.display_name}`
      : player.display_name;
    // Cap at the schema's MAX_TOKEN_LABEL_LENGTH (24) — see
    // `lib/canvas/board-schema.ts:48`.
    const label = labelFromPlayer.slice(0, 24);
    commitScene({
      ...props.scene,
      tokens: props.scene.tokens.map((t) =>
        t.id === tokenId
          ? { ...t, player_id: player.id, label }
          : t,
      ),
    });
  }

  const presets = formationsForGameFormat(gameFormat);

  return (
    <div
      className="flex flex-col lg:flex-row gap-4 w-full"
      data-coach-review-source="tactical_board"
    >
      <div
        className="flex-shrink-0 rounded-lg overflow-hidden border border-rule bg-surface"
        style={{ width, height }}
        data-testid="tactical-board-stage-container"
      >
        <Stage
          ref={stageRef}
          width={width}
          height={height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        >
          <Layer listening={false}>
            <PitchBackground dims={dims} />
          </Layer>
          {/* M7.5 C1: hide the drawing layer when Canvas is "Off".
           * Pitch stays visible; only the shapes / tokens fade. */}
          <Layer visible={canvasVisible}>
            {props.scene.shapes.map((shape, i) => (
              <ShapeRenderer
                key={shape.id ?? `shape-${i}`}
                shape={shape}
                dims={dims}
                selected={shape.id === selectedId}
                onSelect={() => selectShape(shape.id ?? null)}
                draggable={armedTool === "select" && shape.id === selectedId}
                onDragEnd={(s) => replaceShape(s as BoardShape)}
              />
            ))}
            {props.scene.tokens.map((token, i) => (
              <TokenRenderer
                key={token.id ?? `token-${i}`}
                token={token}
                dims={dims}
                selected={token.id === selectedId}
                onSelect={() => selectShape(token.id ?? null)}
                draggable={armedTool === "select" && token.id === selectedId}
                onDragEnd={replaceToken}
              />
            ))}
            {pending ? <ShapeRenderer shape={pending} dims={dims} /> : null}
            <Transformer
              ref={transformerRef}
              rotateEnabled={false}
              enabledAnchors={[
                "top-left",
                "top-center",
                "top-right",
                "middle-right",
                "middle-left",
                "bottom-left",
                "bottom-center",
                "bottom-right",
              ]}
              boundBoxFunc={(oldBox, newBox) => {
                if (newBox.width < 8 || newBox.height < 8) return oldBox;
                return newBox;
              }}
              onTransformEnd={handleTransformEnd}
            />
          </Layer>
        </Stage>
      </div>

      {props.hideToolbar ? null : (
      <div
        className="flex flex-col gap-4 w-full lg:w-[300px] lg:flex-shrink-0 lg:max-h-[var(--tb-stage-h)] lg:overflow-y-auto"
        style={{ "--tb-stage-h": `${height}px` } as React.CSSProperties}
        data-testid="canvas-toolbar-rail"
      >
        <ToolGrid tools={TACTICAL_TOOLS} value={armedTool} onChange={armTool} />
        <ColorPalette value={color} onChange={setColor} />
        <StrokeWidthSlider value={strokeWidth} onChange={setStrokeWidth} />
        <div className="flex flex-col gap-2">
          <span className="text-xs font-display text-ink-muted uppercase tracking-wider">
            Formation
          </span>
          <div className="flex gap-2">
            <CanvasSelect
              value={gameFormat}
              onChange={(e) => {
                const g = e.target.value as GameFormat;
                setGameFormat(g);
                const first = formationsForGameFormat(g)[0];
                if (first) setFormationId(first.id);
              }}
              data-canvas-game-format
              className="flex-shrink-0"
            >
              {GAME_FORMATS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </CanvasSelect>
            <CanvasSelect
              value={formationId}
              onChange={(e) => setFormationId(e.target.value)}
              data-canvas-formation
              className="flex-1 min-w-0"
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </CanvasSelect>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleApplyFormation}
              data-canvas-apply-formation
              className="bg-accent hover:bg-accent-hover text-white rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
            >
              Apply Formation
            </button>
            {/* M7.5 E3: Linked-mode toggle. When on, applying a
             * formation opens the roster picker per new token in
             * sequence so each anchor gets bound to a real player. */}
            <button
              type="button"
              onClick={() => setFormationLinkedMode((v) => !v)}
              data-testid="board-formation-linked-toggle"
              data-active={formationLinkedMode ? "true" : undefined}
              className="rounded border px-2 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-surface data-[active=true]:border-accent data-[active=true]:text-accent data-[active=true]:bg-accent/10"
            >
              Linked
            </button>
          </div>
        </div>
        {/* M7.5 B3: inline label-text input. Coach types text here,
         * then clicks the pitch to drop a label at that point.
         * Visible whenever the Label tool is armed. */}
        {armedTool === "label" ? (
          <label className="flex flex-col gap-1">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Label text
            </span>
            <input
              type="text"
              value={labelText}
              onChange={(e) => setLabelText(e.target.value)}
              maxLength={80}
              placeholder="Type label, then click the pitch"
              data-testid="board-label-text-input"
              className="bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
        ) : null}

        {/* M7.5 E1: per-token inline editor — shown when a player
         * token is selected. Coach can type a label directly OR
         * pick a roster player to auto-fill it + bind `player_id`. */}
        {(() => {
          const sel =
            selectedId &&
            props.scene.tokens.find(
              (t) => t.id === selectedId && t.kind === "player",
            );
          if (!sel) return null;
          return (
            <div
              className="flex flex-col gap-1.5 rounded border border-rule bg-bg-card p-2"
              data-testid="board-player-token-editor"
            >
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Player token
                {sel.player_id ? " · linked" : ""}
              </span>
              <input
                type="text"
                value={sel.label ?? ""}
                onChange={(e) => {
                  const nextLabel = e.target.value.slice(0, 24);
                  commitScene({
                    ...props.scene,
                    tokens: props.scene.tokens.map((t) =>
                      t.id === sel.id ? { ...t, label: nextLabel } : t,
                    ),
                  });
                }}
                maxLength={24}
                placeholder="Token label (#10 Drew)"
                data-testid="board-player-token-label-input"
                className="bg-surface border border-rule rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button
                type="button"
                onClick={() => setBindingTokenId(sel.id ?? null)}
                data-testid="board-player-token-pick"
                className="bg-surface hover:bg-bg-card border border-rule text-ink rounded px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
              >
                {sel.player_id ? "Change player" : "Pick player"}
              </button>
            </div>
          );
        })()}

        {/* M7.5 C1 / C2 / C3: Canvas-rail controls. */}
        <CanvasRailControls
          visible={canvasVisible}
          onToggleVisible={() => setCanvasVisible((v) => !v)}
          undoDepth={undoStack.depth}
          onUndo={() => {
            const prev = undoStack.pop();
            if (prev) props.onSceneChange(prev);
          }}
          hasSelection={selectedId != null}
          onDeleteSelected={() => {
            if (!selectedId) return;
            const nextShapes = props.scene.shapes.filter(
              (s) => s.id !== selectedId,
            );
            const nextTokens = props.scene.tokens.filter(
              (t) => t.id !== selectedId,
            );
            commitScene({
              ...props.scene,
              shapes: nextShapes,
              tokens: nextTokens,
            });
            selectShape(null);
          }}
          testidPrefix="board-rail"
        />

        <div className="flex flex-col gap-2 mt-auto">
          {/* M7.5 B4: styled confirm replaces `window.confirm`. */}
          <button
            type="button"
            onClick={() => setConfirm({ kind: "clear-board" })}
            data-canvas-clear-board
            className="border border-destructive/40 text-destructive hover:bg-destructive/10 rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
          >
            Clear board
          </button>
        </div>
        {props.toolbarChildren ? (
          <div className="flex flex-col gap-4 pt-4 border-t border-rule/40">
            {props.toolbarChildren}
          </div>
        ) : null}
      </div>
      )}

      {/* M7.5 B4 / B6: styled confirms for destructive actions. */}
      {confirm?.kind === "clear-board" ? (
        <ConfirmDialog
          title="Clear the board?"
          message="This removes every token and shape. This can't be undone."
          confirmLabel="Clear board"
          danger
          onConfirm={() => {
            commitScene(emptyScene());
            selectShape(null);
            setConfirm(null);
          }}
          onClose={() => setConfirm(null)}
          data-testid="board-clear-confirm"
        />
      ) : null}
      {confirm?.kind === "apply-formation" ? (
        <ConfirmDialog
          title="Apply this formation?"
          message="Existing player tokens will be replaced. Ball and shapes stay."
          confirmLabel="Apply formation"
          danger
          onConfirm={() => {
            const target = confirm.pendingFormationId;
            setConfirm(null);
            commitApplyFormation(target);
          }}
          onClose={() => setConfirm(null)}
          data-testid="board-apply-formation-confirm"
        />
      ) : null}

      {/* M7.5 E1: single-bind roster picker, anchored to a
       * selected player token. */}
      {bindingTokenId ? (
        <RosterPickerModal
          mode="single"
          title="Bind player to token"
          activeOnly
          onConfirm={(player) => {
            bindPlayerToToken(bindingTokenId, player);
            setBindingTokenId(null);
          }}
          onClose={() => setBindingTokenId(null)}
          data-testid="board-token-bind-picker"
        />
      ) : null}

      {/* M7.5 E3: Linked-mode sequential apply — opens the
       * picker per new anchor in order. */}
      {linkingFormation ? (
        <RosterPickerModal
          mode="single"
          title={`Bind player ${linkingFormation.index + 1} of ${linkingFormation.tokenIds.length}`}
          activeOnly
          onConfirm={(player) => {
            const { tokenIds, index } = linkingFormation;
            bindPlayerToToken(tokenIds[index], player);
            const nextIndex = index + 1;
            if (nextIndex < tokenIds.length) {
              setLinkingFormation({ tokenIds, index: nextIndex });
            } else {
              setLinkingFormation(null);
            }
          }}
          // "Cancel" stops the sequence — partial binds are kept.
          onClose={() => setLinkingFormation(null)}
          data-testid="board-formation-linked-picker"
        />
      ) : null}
    </div>
  );
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
